import React, {
  useState, useCallback, useRef, useEffect,
} from 'react';
import {useField, useFormikContext} from 'formik';
import _ from 'lodash';

import {StyledTextArea} from '../../components/Forms';
import {getQuestionsRelatedToString} from '../../api/api';
import {cx} from '../../util';

const MIN_CHARS_TO_SUGGEST = 3;

const TextAreaWithRelatedQuestions = ({
  name, question, onBlur, autoFocus,
}) => {
  const textInput = useRef(null);
  const [open, setOpen] = useState(false);
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const [field] = useField(`${name}.text`);
  const {setFieldValue} = useFormikContext();
  const selectedId = question?.existing;

  const adjustHeight = useCallback(() => {
    const textarea = textInput.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, 39)}px`; // Match h-10 of the question number square
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [field.value, adjustHeight]);

  const suggestSimilarQuestions = useCallback(_.debounce(
    async (value) => {
      const questions = await getQuestionsRelatedToString(value);
      setRelatedQuestions(questions.sort((a, b) => b.votes - a.votes));
      setOpen(true);
    },
    500,
  ), []);

  const handleChange = (e) => {
    const {target: {value}} = e;
    // Clear existing selection if user modifies text
    if (selectedId) {
      setFieldValue(`${name}.existing`, undefined);
    }
    if (value.length >= MIN_CHARS_TO_SUGGEST) suggestSimilarQuestions(value);
    field.onChange(e);
  };

  const handleSelectRelated = async (q) => {
    await setFieldValue(`${name}.existing`, q.id);
    await setFieldValue(`${name}.text`, q.data.title);
    setOpen(false);
    textInput.current?.blur();
  };

  // Needed because: https://stackoverflow.com/a/57630197/3681279
  const preventDefault = (e) => e.preventDefault();

  return (
    <div className="flex relative">
      <StyledTextArea
        {...field}
        name={`${name}.text`}
        placeholder="Add question"
        onChange={handleChange}
        onBlur={() => { setOpen(false); if (onBlur) onBlur(); }}
        onFocus={() => setOpen(true)}
        ref={textInput}
        autoComplete="off"
        autoFocus={autoFocus}
        rows={1}
        style={{
          overflow: 'hidden', resize: 'none', minHeight: '39px',
        }}
      />
      {open && relatedQuestions.length > 0 && (
        <div className="absolute top-full z-10 mt-1 left-0 right-0 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <ul className="max-h-60 overflow-auto">
            {relatedQuestions.map((q) => (
              <li key={q.id}>
                <button
                  type="button"
                  onMouseDown={preventDefault}
                  onClick={() => handleSelectRelated(q)}
                  className={cx(
                    'w-full px-4 py-3 text-left text-gray-900 hover:bg-gray-50',
                    selectedId === q.id && 'text-indigo-600',
                  )}
                >
                  {q.data.title}
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onMouseDown={preventDefault}
            onClick={() => setOpen(false)}
            className="w-full px-4 py-3 text-center text-indigo-600 hover:bg-gray-50 font-medium text-sm"
          >
            + Add new
          </button>
        </div>
      )}
    </div>
  );
};

export default TextAreaWithRelatedQuestions;
